/**
 * Home — the Naumene landing page.
 * @file src/web/features/main/pages/index.tsx
 *
 * ── The argument ──────────────────────────────────────────────────────────
 * Every product in this category sells a form builder. A firm comparing them
 * ends up comparing field types. Building the form was never the expensive
 * part — losing the person halfway through it is. So the page leads with the
 * funnel, and is organised in three tiers, deliberately:
 *
 *   1. The promise      three lines, the whole product
 *   2. What sticks      the five things a firm cannot get elsewhere
 *   3. What is expected the table stakes, in one dense grid
 *
 * A reader who leaves after tier one has the pitch. One who reads tier two
 * knows why we exist. Tier three exists so nobody has to ask "but does it do
 * booking" — and it answers with a status per row rather than a claim.
 *
 * ── Why the page was re-tiered ────────────────────────────────────────────
 * It used to tell the drop-off story five times (hero, promises, steps, two
 * stalls, behaviours band) and the language story three. Repetition read as
 * padding and buried the two features nobody else has — scoring on the
 * firm's own rules, and document collection that files itself. Each idea now
 * appears once, in the tier that earns it.
 *
 * ── Honesty about what ships ──────────────────────────────────────────────
 * Tier two and tier three both carry a status per row. Document collection
 * and CMS sync are marked coming, because a firm that adopted this expecting
 * either would find out on its first matter.
 *
 * ── Design notes ──────────────────────────────────────────────────────────
 * Fluid type (clamp) so headings scale with the viewport rather than jumping
 * at breakpoints. Tier two alternates side to side, which gives the middle of
 * the page a rhythm instead of nine identical cards. Gold stays an accent —
 * hairlines, ticks, the underline — per the brand kit's "never a large fill".
 *
 * @llm-rule WHEN: Editing Naumene marketing copy
 * @llm-rule AVOID: Marking a capability available before it is built
 * @llm-rule AVOID: Inventing conversion statistics; the only numbers here are
 *   shapes from the demo data, and they are labelled as such
 * @llm-rule AVOID: Telling the same story in two tiers — that is what this
 *   rewrite removed
 * @llm-rule NOTE: Routed by FBCA — this file IS /
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, cn } from '@bloomneo/uikit';
import {
  ArrowRight,
  BellRing,
  Check,
  Eye,
  FolderInput,
  Gauge,
  Languages,
  TrendingDown,
  Users,
} from 'lucide-react';
import { SEO } from '@/shared/SEO';
import { brand } from '@/shared/brand';
import { productSchema } from '@/shared/structured-data';
/* One price list for the whole site — see the note in pricing.ts about why
 * the home page must not keep its own copy of these numbers. */
import { ANNUAL_SAVING, PRICE } from '@/shared/pricing';
/* Same reasoning for the capability inventory: /features renders these
 * grouped, this page renders them flat, and `ready` must not be able to
 * disagree between the two. */
import { BEHAVIOURS, CAPABILITIES } from '@/shared/capabilities';
import { ProductFrame } from '../components/ProductFrame';

/** The public demo form, opened in Spanish — the best proof on the site. */

/**
 * What follows "Every" in the headline.
 *
 * The anchor word holds still and the rest is typed, so the promise reads as
 * one sentence with three endings rather than three separate slides. Each is
 * a noun and a past participle — what a finished job sounds like — and they
 * run in the order the work does: the lead, then the paperwork, then the
 * matter.
 */
const PHRASES = ['lead, chased.', 'document, collected.', 'matter, opened.'] as const;

/*
 * Typing speeds, in milliseconds. Typing is slower than erasing on purpose:
 * a phrase being written is being read, and one being taken away is not.
 */
const TYPE_MS = 55;
const ERASE_MS = 26;
const HOLD_MS = 1800;
const BETWEEN_MS = 280;


/**
 * The light behind the hero.
 *
 * Two slow blooms and a masked grid, drawn in CSS — depth with nothing to
 * download, and it stays sharp at any density. Ivory lines rather than the
 * foreground token, because this sits on navy in both themes.
 */
function HeroGlow() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        className="absolute -left-40 -top-56 size-[44rem] rounded-full opacity-30 blur-3xl"
        style={{ background: 'radial-gradient(circle, oklch(0.538 0.109 261.7), transparent 64%)' }}
      />
      <div
        className="absolute -right-40 top-10 size-[34rem] rounded-full opacity-20 blur-3xl"
        style={{ background: 'radial-gradient(circle, var(--hero-gold), transparent 66%)' }}
      />
      <div
        className="absolute inset-0 opacity-[0.055]"
        style={{
          backgroundImage:
            'linear-gradient(var(--hero-fg) 1px, transparent 1px),' +
            'linear-gradient(90deg, var(--hero-fg) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage: 'radial-gradient(ellipse 90% 70% at 50% 0%, #000 40%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 90% 70% at 50% 0%, #000 40%, transparent 100%)',
        }}
      />
    </div>
  );
}

/**
 * The eyebrow, with a gold tick in front of it.
 *
 * The brand kit is explicit that gold is "accent ONLY — never a large fill",
 * so it earns its keep in small, repeated marks like this one.
 */
function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
      <span className="h-px w-6 bg-warning" aria-hidden="true" />
      {children}
    </span>
  );
}

/** An ivory band — the palette's warm off-white, used to give the page a rhythm. */
function Band({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <section className={`-mx-4 bg-accent px-4 py-20 sm:-mx-6 sm:px-6 sm:py-24 ${className}`}>
      {children}
    </section>
  );
}

type IconType = React.ComponentType<{ className?: string }>;

/** A tinted square behind an icon. */
function Chip({ icon: Icon, tone = 'navy' }: { icon: IconType; tone?: 'navy' | 'gold' }) {
  return (
    <span
      className={`grid size-10 place-items-center rounded-xl ${
        tone === 'gold'
          ? 'bg-warning/15 text-warning-foreground dark:text-warning'
          : 'bg-primary/10 text-primary'
      }`}
    >
      <Icon className="size-5" />
    </span>
  );
}

/** Section headings in the display serif, sized fluidly. */
function SectionHead({ eyebrow, title, lead }: { eyebrow: string; title: string; lead?: string }) {
  return (
    <div className="max-w-2xl">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="mt-4 font-display text-[clamp(1.85rem,3.4vw,2.6rem)] font-semibold leading-[1.12]">
        {title}
      </h2>
      {lead && <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{lead}</p>}
    </div>
  );
}

/* ── Tier one: the three promises. Same words on the About page. ────────── */
const PROMISES = [
  {
    icon: Eye,
    title: 'We see them stop.',
    body: 'Every enquiry is a row from the first keystroke — where they stopped, what they were asked, what to send next.',
  },
  {
    icon: BellRing,
    title: 'We chase them back.',
    body: 'Each kind of stall gets its own message, sent in your name, and you see it before it goes.',
  },
  {
    icon: Users,
    title: 'Cut your costs by half.',
    body: 'More in the price, not less — every feature included, no caps, nothing held back. $29 per user, per month.',
  },
];

/* ── How it works — a real sequence, so the numbers carry information. ─── */
const STEPS = [
  {
    title: 'They start, through any door',
    body: 'A link, your website, an emailed invitation, your own API. On a phone, at night, in their language.',
  },
  {
    title: 'It becomes a row as they type',
    body: 'Saved on every answer. If they leave, you have the five answers they gave and the question they left at.',
  },
  {
    title: 'It notices the stall and chases',
    body: 'Finished but never signed, or signed but never finished — told apart, each nudged with the right message.',
  },
  {
    title: 'You open the matter',
    body: 'One click hands the retained client to your matter system. Nothing re-keyed.',
  },
];

export default function Home() {
  /*
   * Hand the ground back.
   *
   * index.html paints the document navy before anything runs, so the first
   * frame of this page is its own hero rather than a white flash. Once the
   * hero is actually on screen that colour has no more work to do — and
   * leaving it would show navy under the light half of the page whenever
   * somebody overscrolls past the footer.
   */
  useEffect(() => {
    document.documentElement.removeAttribute('data-landing');
  }, []);

  return (
    <div className="space-y-24 pb-24 sm:space-y-32">
      {/*
        * The title carries the category, not the brand.
        *
        * It was `brand.productName` — "Naumene" — which is the one string
        * a search engine already knows us by and the one a firm shopping for
        * this has never typed. The words they do type are "client intake
        * software" and "law firm", so those lead and the brand follows.
        */}
      <SEO
        title={`Client intake software for law firms | ${brand.productName}`}
        description="Client intake that tells you which enquiries you are losing, and why — because an abandoned form is a record here, not an absence. $29 per user, per month, with every feature included."
        jsonLd={productSchema()}
      />

      <style>{`
        @keyframes naumene-caret { 0%, 55% { opacity: 1 } 56%, 100% { opacity: 0 } }
        /* A cursor is on or it is off — steps, not a fade. */
        .naumene-caret { animation: naumene-caret 1.05s steps(1, end) infinite; }
        @media (prefers-reduced-motion: reduce) { .naumene-caret { animation: none; opacity: 0; } }
      `}</style>

      {/*
        * ── hero ───────────────────────────────────────────────────────────
        *
        * A stage, not a card. Full-bleed via `ml-[calc(50%-50vw)] w-screen`
        * — cancelling the gutter with negative margins still stopped short
        * of both edges inside the shell's max-width and read as a very large
        * box. The negative top margin takes it up under the navy header, so
        * the bar and the hero are one surface rather than two navy things
        * with a light seam between them.
        */}
      <section className="mb-12 sm:mb-16 hero-navy relative -mt-8 ml-[calc(50%-50vw)] w-screen overflow-hidden bg-[var(--hero-bg)] pb-16 pt-24 text-[color:var(--hero-fg)] sm:-mt-10 sm:pb-20 sm:pt-32">
        <HeroGlow />

        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-[1.05fr_1fr]">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-[color:var(--hero-gold)]/30 bg-[var(--hero-gold)]/10 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.14em] text-[color:var(--hero-gold)]">
              <span className="size-1.5 rounded-full bg-[var(--hero-gold)]" aria-hidden="true" />
              Client intake software for law firms
            </span>

            <TypedHeadline />

            {/* The scene, then the loss, then what changes — the last clause
              * has to be the thing the firm GETS, not the data structure we
              * keep it in. "They are a row" described our schema. */}
            {/*
              * Who it is for, then what it does — one sentence.
              *
              * Track, nurture, convert is the vocabulary a firm already uses
              * about its own pipeline, so it places the product without
              * explaining it, and "mean to win the work" says who it was
              * drawn for without the word "modern". Written as participles
              * rather than a colon and a list: a hero line should read like a
              * sentence somebody said, not a slide.
              */}
            <p className="mt-7 max-w-xl text-xl leading-relaxed text-[color:var(--hero-muted)]">
              Purpose-built intake for law firms that mean to win the work — tracking every
              enquiry, nurturing the quiet ones, converting more of them.
            </p>

            {/*
              * The wedge, on its own line, directly under the claim: every
              * product in this category costs a migration, and this is the
              * sentence that makes the claim above buyable.
              *
              * Only names that are built. This said "Clio, Filevine, MyCase"
              * while the connectors were an empty array — and MyCase never
              * existed even as a stub. The rule was that a second name goes
              * back when a second connector opens a real matter: Filevine
              * now does, so it is here.
              *
              * MyCase is still NOT here, and "and more" is not either. Its
              * handshake is written and verified; the calls that would open
              * a case are not, because MyCase keeps that documentation
              * behind a login. This is the easiest claim on the page for a
              * buyer to check, and the most expensive one to be caught on.
              */}
            <p className="mt-5 max-w-xl text-lg font-medium leading-relaxed">
              Works with the Clio or Filevine you already run.
              {/* Its own line rather than a trailing clause: the objection
                * this answers — "we would have to move everything" — is the
                * one that ends the conversation, and an answer wrapped onto
                * the end of the sentence before it reads as a footnote to a
                * claim instead of the reply to a fear. `block` rather than
                * <br /> so it keeps the paragraph's line height. */}
              <span className="block text-[color:var(--hero-gold)]">Nothing to migrate.</span>
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Button
                size="lg"
                asChild
                className="bg-[var(--hero-gold)] text-[color:var(--hero-ink)] hover:bg-[var(--hero-gold)] hover:text-[color:var(--hero-ink)] hover:brightness-105"
              >
                <Link to="/auth/register">
                  Get started now
                  <ArrowRight className="ml-1.5 size-4" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                asChild
                className="border-[color:var(--hero-line)] bg-transparent text-[color:var(--hero-fg)] hover:bg-[var(--hero-hover)] hover:text-[color:var(--hero-fg)]"
              >
                <a href="#how-it-works">How it works</a>
              </Button>
            </div>

            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[color:var(--hero-muted)]">
              {[
                'Saves as they type',
                'Knows why they stopped',
                'Scores them on your rules',
                'Opens the matter for you',
              ].map((f) => (
                <li key={f} className="flex items-center gap-1.5">
                  <Check className="size-3.5 text-[color:var(--hero-gold)]" aria-hidden="true" />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:pl-4">
            <ProductFrame />
          </div>
        </div>

        {/*
          * The doors, where a category page would put a logo wall.
          *
          * It does the same visual job — a quiet strip of proof under the
          * fold — and every item on it is verifiable today, which a customer
          * logo wall would not be. When the Clio connector ships this is
          * where the systems list belongs instead.
          */}
        <div className="relative mx-auto mt-16 max-w-6xl px-4 sm:px-6">
          <div className="border-t border-[color:var(--hero-line)] pt-6">
          <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-[color:var(--hero-faint)]">
            Four ways an enquiry reaches you — one pipeline
          </span>
          <div className="mt-3 flex flex-wrap gap-x-7 gap-y-2">
            {['A link you send', 'Your website', 'An emailed invitation', 'Your own API'].map((c) => (
              <span key={c} className="text-sm text-[color:var(--hero-muted)]">
                {c}
              </span>
            ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── tier one: the three promises ─────────────────────────────────── */}
      {/* Closer to the hero and to How it works than the page's usual section gap:
        * it is a strip under the fold, not a section of its own. */}
      <section aria-label="What we promise" className="mb-12 sm:mb-16">
        <div className="grid gap-px overflow-hidden rounded-3xl border border-border bg-border md:grid-cols-3">
          {PROMISES.map(({ icon: Icon, title, body }) => (
            <div key={title} className="bg-card p-7 transition-colors hover:bg-accent/40">
              <div className="flex items-center gap-3">
                <span className="h-px w-6 bg-warning" aria-hidden="true" />
                <Icon className="size-4 text-primary" aria-hidden="true" />
              </div>
              <h2 className="mt-4 font-display text-2xl font-semibold">{title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── how it works ─────────────────────────────────────────────────── */}
      <section id="how-it-works" className="scroll-mt-24">
        <SectionHead
          eyebrow="How it works"
          title="Four steps, and the second one is the product."
          lead="Everyone in this category does the first and the last. The middle two — a row before they finish, a chase that knows why they stopped — are the difference."
        />

        <ol className="relative mt-12 grid gap-8 md:grid-cols-4 md:gap-6">
          <span
            aria-hidden="true"
            className="absolute left-5 top-0 hidden h-px w-[calc(100%-2.5rem)] bg-border md:block"
          />
          {STEPS.map((s, i) => {
            const core = i === 1 || i === 2;
            return (
              <li key={s.title} className="relative md:pt-8">
                <span
                  className={`absolute left-0 top-0 hidden size-2.5 -translate-y-1/2 rounded-full md:block ${
                    core ? 'bg-warning' : 'bg-border'
                  }`}
                  aria-hidden="true"
                />
                <div className="font-mono text-[11px] tabular-nums text-muted-foreground">
                  0{i + 1}
                </div>
                <h3 className="mt-2 font-semibold">{s.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
              </li>
            );
          })}
        </ol>
      </section>

      {/* ── tier two: what a firm cannot get elsewhere ───────────────────── */}
      <section>
        <SectionHead
          eyebrow="What keeps a firm here"
          title="Five things the rest of the category does not do."
          lead="Not a feature list — these are the reasons a firm that tries this does not go back."
        />

        <div className="mt-14 space-y-16 sm:space-y-20">
          {/* 1 — drop-off, the thing nobody else computes */}
          <Sticky
            n="01"
            icon={TrendingDown}
            title="You see the question they quit on."
            body="Not “the lead went cold”. Question six, at 11.40pm, after five answers you already have. Every keystroke is saved as they type, so an abandoned form is a record rather than an absence — and the form’s own card names the question that loses the most people."
            points={[
              'Every answer saved as it is typed',
              'A stop point per person, a worst question per form',
              'Completion and signature rates, already divided',
            ]}
            media={
              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  Personal injury enquiry · where they stop
                </div>
                <ol className="mt-5 space-y-2 text-sm">
                  {[
                    { q: 'Your full name', left: 0 },
                    { q: 'Email address', left: 2 },
                    { q: 'What happened?', left: 1 },
                    { q: 'Were you injured?', left: 0 },
                    { q: 'Who referred you?', left: 11 },
                    { q: 'Upload the police report', left: 4 },
                  ].map((r, i) => (
                    <li
                      key={r.q}
                      className={`flex items-center justify-between gap-4 rounded-lg border px-3 py-2 ${
                        r.left > 6 ? 'border-warning/50 bg-warning/10' : 'border-border'
                      }`}
                    >
                      <span className="flex items-center gap-2.5">
                        <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
                          0{i + 1}
                        </span>
                        {r.q}
                      </span>
                      <span className="shrink-0 tabular-nums text-xs text-muted-foreground">
                        {r.left === 0 ? '—' : `${r.left} left here`}
                      </span>
                    </li>
                  ))}
                </ol>
                <p className="mt-5 border-t border-border pt-4 text-xs leading-relaxed text-muted-foreground">
                  Eleven of eighteen abandonments on this form happen at one question. It is the
                  fifth of six, and it is optional.
                </p>
              </div>
            }
          />

          {/* 2 — scoring, on the firm's rules */}
          <Sticky
            n="02"
            icon={Gauge}
            flip
            title="Scored on your rules, with the reason shown."
            body="You write the rules — matter type, claim value, how fast they replied, what the answers actually said. Every enquiry arrives with a number and the sentences behind it, so a paralegal can see why this one is at the top and disagree with it out loud."
            points={[
              'Rules you write and edit, not a black box',
              'The reasons stored on the lead, not just the score',
              'Assignment follows the score, round-robin and fair',
            ]}
            media={
              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="text-sm font-medium">Ana Ruiz</div>
                    <div className="text-xs text-muted-foreground">
                      Personal injury · Spanish · web form
                    </div>
                  </div>
                  <div className="font-display text-4xl tabular-nums text-primary">82</div>
                </div>
                <ul className="mt-5 space-y-2 text-sm">
                  {[
                    ['Claim over $20,000', '+15'],
                    ['Answered within 10 minutes', '+12'],
                    ['Matter type we take', '+20'],
                    ['No conflict found', '+10'],
                    ['Out of state', '−5'],
                  ].map(([reason, pts]) => (
                    <li
                      key={reason}
                      className="flex items-center justify-between gap-4 border-b border-border pb-2 last:border-0"
                    >
                      <span className="text-muted-foreground">{reason}</span>
                      <span className="shrink-0 font-mono text-xs tabular-nums">{pts}</span>
                    </li>
                  ))}
                </ul>
              </div>
            }
          />

          {/* 3 — the chasing, by reason */}
          <Sticky
            n="03"
            icon={BellRing}
            title="Chased for the right reason, not on a timer."
            body="A stalled client is not one problem, it is two. Finished the form but never signed is a different person from signed but never finished, and they need opposite messages. Five behaviours are switched on from day one; you edit the words, never a blank canvas."
            points={[
              'One email per person per sweep, guaranteed by the database',
              'Stops the moment they reply, sign, or finish',
              'You see who it would reach before you arm it',
            ]}
            media={
              <ol className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
                {BEHAVIOURS.map((b) => (
                  <li key={b.when} className="grid gap-1.5 p-5 sm:grid-cols-[1fr_1.3fr] sm:gap-5">
                    <div>
                      <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                        When
                      </div>
                      <div className="mt-0.5 text-sm font-medium">{b.when}</div>
                    </div>
                    <div>
                      <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                        Then
                      </div>
                      <div className="mt-0.5 text-sm leading-relaxed text-muted-foreground">
                        {b.then}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            }
          />

          {/* 4 — documents.
            *
            * All three bullets are now true. The per-matter-type list is
            * src/shared/document-checklists.ts, requested in one click from
            * the lead; reminders go out on the hourly sweep and name only
            * the required items still missing; and what arrives is filed to
            * the matter in Clio or Filevine by matter-document.service. The
            * mock list beside this card is illustrative, not live data. */}
          <Sticky
            n="04"
            icon={FolderInput}
            flip
            title="Documents that file themselves."
            body="Ask for the six things this matter needs, and let the chasing happen without anybody opening a mailbox. What arrives lands on the matter named properly — instead of an inbox of attachments called IMG_2841.jpg that somebody has to rename on a Friday."
            points={[
              'A list per matter type, requested in one click',
              'Reminders only for what has not arrived',
              'Filed to the matter, not downloaded and re-uploaded',
            ]}
            media={
              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  Requested from an inbound lead
                </div>
                <ul className="mt-5 space-y-2 text-sm">
                  {[
                    ['Police report', 'received'],
                    ['Photos of the vehicle', 'received'],
                    ['Medical records — ER', 'chased twice'],
                    ['Insurance declaration page', 'waiting'],
                    ['Wage records', 'waiting'],
                  ].map(([doc, state]) => (
                    <li key={doc} className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-2.5">
                        <span
                          className={`size-1.5 rounded-full ${
                            state === 'received' ? 'bg-success' : 'bg-warning'
                          }`}
                          aria-hidden="true"
                        />
                        {doc}
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground">{state}</span>
                    </li>
                  ))}
                </ul>
              </div>
            }
          />

          {/* 5 — language, demoted to where it belongs: real, not the headline */}
          <Sticky
            n="05"
            icon={Languages}
            title="Your questions in their language — not our buttons."
            body="Competitors translate their own chrome and leave your questions in English. A form can then advertise Spanish and hand a frightened person a sentence they cannot read, thirty seconds from the finish line. Your copy is stored per language, and the form’s card tells you which questions are missing which translation."
            points={[
              'Questions, retainers and emails, per language',
              'A coverage warning before a client finds it for you',
              'Thirty-three languages, and the client picks',
            ]}

            media={
              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                    Completion by language
                  </div>
                  <span className="inline-flex items-center rounded-md border border-warning/50 bg-warning/10 px-2 py-1 font-mono text-[10px] text-warning-foreground dark:text-warning">
                    1 question untranslated
                  </span>
                </div>
                <div className="mt-5 space-y-4">
                  {[
                    { lang: 'English', pct: 79, n: '11 of 14' },
                    { lang: 'Русский', pct: 43, n: '3 of 7' },
                    { lang: 'Español', pct: 33, n: '3 of 9' },
                  ].map((r) => (
                    <div key={r.lang}>
                      <div className="flex items-baseline justify-between text-sm">
                        <span>{r.lang}</span>
                        <span className="tabular-nums text-muted-foreground">
                          {r.pct}% <span className="text-xs">({r.n})</span>
                        </span>
                      </div>
                      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-muted">
                        <div
                          className={`h-full rounded-full ${r.pct > 60 ? 'bg-chart1' : 'bg-warning'}`}
                          style={{ width: `${r.pct}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-5 border-t border-border pt-4 text-xs leading-relaxed text-muted-foreground">
                  Question five, “Who referred you?”, has no Spanish or Russian translation. Every
                  other question on the form does.
                </p>
              </div>
            }
          />
        </div>
      </section>

      {/* ── the numbers — the demo firm's, and labelled as such ─────────── */}
      <Band>
        <div className="text-center">
          <Eyebrow>From the demo firm that ships with every install</Eyebrow>
          <div className="mt-10 grid gap-10 sm:grid-cols-3">
            {[
              { n: '18', label: 'forms started and abandoned', sub: 'every one of them a row you can open' },
              { n: '9', label: 'prospects stalled, two different ways', sub: 'chased with two different messages' },
              { n: '33%', label: 'Spanish completion on one form', sub: 'against 79% in English' },
            ].map((k) => (
              <div key={k.label}>
                <div className="font-display text-[clamp(3rem,7vw,4.5rem)] leading-none tabular-nums text-primary">
                  {k.n}
                </div>
                <div className="mt-3 font-medium">{k.label}</div>
                <div className="mt-1 text-sm text-muted-foreground">{k.sub}</div>
              </div>
            ))}
          </div>
          <p className="mx-auto mt-10 max-w-xl text-sm text-muted-foreground">
            Thirty-six prospects, ninety days, four forms — the dataset every install starts with,
            not a customer’s numbers. The point is that every one of these is a number a firm can
            normally never see.
          </p>
          <div className="mt-6">
            <Button variant="outline" asChild>
              <Link to="/contact">Ask us for a walkthrough</Link>
            </Button>
          </div>
        </div>
      </Band>

      {/* ── tier three: the table stakes, in one grid ────────────────────── */}
      <section>
        <SectionHead
          eyebrow="The rest of it"
          title="And everything you would expect it to have."
          lead="Booking pages, e-signed retainers, notes, conflict checks, a pipeline you arrange yourself. Table stakes — listed once, densely, rather than sold as features."
        />

        {/* One block, not cards: the gap is a hairline of the border colour
          * showing through, which is also why a short last row reads as a
          * hole rather than as whitespace. So the last tile stretches to fill
          * whatever the column count left over — derived, so adding a row
          * later cannot quietly re-open the hole. */}
        <div className="mt-10 grid gap-px overflow-hidden rounded-3xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
          {CAPABILITIES.map((c, i) => (
            <div
              key={c.name}
              className={cn(
                'flex gap-3 bg-card p-5 transition-colors hover:bg-accent/40',
                i === CAPABILITIES.length - 1 && [
                  CAPABILITIES.length % 2 === 1 && 'sm:col-span-2',
                  CAPABILITIES.length % 3 === 1 && 'lg:col-span-3',
                  CAPABILITIES.length % 3 === 2 && 'lg:col-span-2',
                  CAPABILITIES.length % 3 === 0 && 'lg:col-span-1',
                ],
              )}
            >
              <Chip icon={c.icon} />
              <div className="min-w-0">
                <h3 className="text-sm font-semibold">{c.name}</h3>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{c.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── who it is for ───────────────────────────────────────────────── */}
      <section className="rounded-3xl border border-border bg-card px-6 py-10 sm:px-10">
        <div className="grid gap-8 md:grid-cols-[1fr_1.4fr] md:items-center">
          <SectionHead
            eyebrow="Who it is for"
            title="Firms that take enquiries at volume, and cannot afford to lose them quietly."
          />
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              { k: 'Personal injury', v: 'Records to gather, retainers to chase, and the most abandoned forms in law.' },
              { k: 'Immigration', v: 'Clients who chose Spanish or Russian, and a form that has to mean it.' },
              { k: 'Family', v: 'People at their worst moment, on a phone, at night. The form has to wait for them.' },
              { k: 'Employment', v: 'Deadlines in the story that nobody put on the form.' },
            ].map((p) => (
              <div key={p.k} className="border-l-2 border-warning/60 pl-4">
                <div className="font-semibold">{p.k}</div>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{p.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── pricing ─────────────────────────────────────────────────────── */}
      <section>
        <SectionHead
          eyebrow="Pricing"
          title="Cut your intake costs by half."
          lead="$29 per user, per month, with every feature in it — drop-off detection, 33 languages, e-signature, conflict checks. No tier to climb to, no cap to hit, nothing to add on. The side-by-side arithmetic is on the pricing page."
        />

        {/*
          * One band, not a card in a corner.
          *
          * This was a `max-w-md` card sitting alone on a full-width row, so
          * two thirds of the section was empty and the eye read the gap as
          * something that had failed to load. The three things it has to say
          * — the number, what is in it, how to start — sit along the row
          * instead, which fills it because there genuinely is that much to
          * say rather than because it was padded out.
          */}
        <div className="mt-10 grid items-center gap-8 rounded-3xl border border-primary bg-card p-8 shadow-lg shadow-primary/5 sm:p-10 lg:grid-cols-[auto_1fr_auto] lg:gap-12">
          <div>
            <div className="flex items-baseline gap-2">
              <span className="font-display text-5xl tabular-nums">{PRICE.amount}</span>
              <span className="text-sm text-muted-foreground">{PRICE.per}</span>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Or pay for the year and it is {ANNUAL_SAVING} less.
            </p>
          </div>

          <dl className="grid gap-x-8 gap-y-2.5 text-sm sm:grid-cols-2 lg:border-x lg:border-border lg:px-12">
            {[
              'Every feature included',
              'Unlimited intake',
              'Unlimited e-signatures',
              'No onboarding fee',
            ].map((line) => (
              <div key={line} className="flex items-baseline gap-2">
                <dt className="sr-only">Included</dt>
                <Check className="mt-0.5 size-3.5 shrink-0 text-primary" aria-hidden="true" />
                <dd>{line}</dd>
              </div>
            ))}
          </dl>

          <Button asChild size="lg" className="w-full sm:w-auto">
            <Link to="/pricing">
              See the pricing <ArrowRight className="ml-1.5 size-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* ── close ───────────────────────────────────────────────────────── */}
      <section className="rounded-3xl border border-border bg-accent px-6 py-16 text-center sm:px-10">
        <h2 className="mx-auto max-w-2xl font-display text-[clamp(1.75rem,3.4vw,2.4rem)] font-semibold leading-[1.15]">
          You already know how many enquiries you got last month.
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-lg leading-relaxed text-muted-foreground">
          This is about the ones that are still sitting there, half-finished, waiting for somebody
          to notice.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button size="lg" asChild>
            <Link to="/auth/register">
              Get started now <ArrowRight className="ml-1.5 size-4" />
            </Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <Link to="/contact">Talk to us</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}

/**
 * One tier-two row: the claim on one side, the proof on the other.
 *
 * Alternating with `flip` rather than nine identical cards — the middle of a
 * landing page is where a reader decides whether to keep going, and a rhythm
 * is what keeps them moving. The media is a real screen shape, not a
 * decorative illustration: each one is something the product actually shows.
 */
function Sticky({
  n,
  icon,
  title,
  body,
  points,
  media,
  flip = false,
}: {
  n: string;
  icon: IconType;
  title: string;
  body: string;
  points: string[];
  media: React.ReactNode;
  flip?: boolean;
}) {
  return (
    <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
      <div className={flip ? 'lg:order-2' : ''}>
        <div className="flex items-center gap-3">
          <Chip icon={icon} />
          <span className="font-mono text-[11px] tabular-nums text-muted-foreground">{n}</span>
        </div>
        <h3 className="mt-5 font-display text-[clamp(1.5rem,2.6vw,2rem)] font-semibold leading-[1.15]">
          {title}
        </h3>
        <p className="mt-4 leading-relaxed text-muted-foreground">{body}</p>
        <ul className="mt-5 space-y-2 text-sm">
          {points.map((p) => (
            <li key={p} className="flex items-start gap-2">
              <Check className="mt-0.5 size-3.5 shrink-0 text-success" aria-hidden="true" />
              <span className="text-muted-foreground">{p}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className={flip ? 'lg:order-1' : ''}>{media}</div>
    </div>
  );
}

/**
 * "Every …", with the rest typed.
 *
 * ── Why the anchor holds ──────────────────────────────────────────────────
 * Typing the whole line made the reader wait for the subject as well as the
 * claim, and three stacked lines said everything at once but read as a list.
 * Holding "Every" still and typing the rest keeps one sentence on screen at
 * all times — the eye has something to rest on while the claim changes.
 *
 * ── What keeps it honest ──────────────────────────────────────────────────
 * All three endings are in the DOM for a screen reader, so the page says them
 * with JavaScript off and with motion turned off — where the first one is
 * simply there, complete, and nothing moves.
 *
 * ── What keeps it still ───────────────────────────────────────────────────
 * An invisible copy of the longest phrase shares the grid cell and reserves
 * the box, so the paragraph below never jumps as the text grows and shrinks.
 */
function TypedHeadline() {
  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const [index, setIndex] = useState(0);
  const [count, setCount] = useState(reduced ? PHRASES[0].length : 0);
  const [erasing, setErasing] = useState(false);

  useEffect(() => {
    if (reduced) return;
    const phrase = PHRASES[index];

    if (!erasing && count === phrase.length) {
      const t = setTimeout(() => setErasing(true), HOLD_MS);
      return () => clearTimeout(t);
    }
    if (erasing && count === 0) {
      const t = setTimeout(() => {
        setIndex((i) => (i + 1) % PHRASES.length);
        setErasing(false);
      }, BETWEEN_MS);
      return () => clearTimeout(t);
    }

    const t = setTimeout(
      () => setCount((c) => c + (erasing ? -1 : 1)),
      erasing ? ERASE_MS : TYPE_MS,
    );
    return () => clearTimeout(t);
  }, [count, erasing, index, reduced]);

  const longest = PHRASES.reduce((a, b) => (a.length >= b.length ? a : b));

  return (
    <h1 className="mt-6 whitespace-nowrap text-[clamp(1.35rem,2.65vw,2.3rem)] font-semibold leading-[1.14]">
      <span className="sr-only">
        Every lead, chased. Every document, collected. Every matter, opened.
      </span>

      <span aria-hidden className="grid">
        <span className="col-start-1 row-start-1">
          Every{' '}
          <span className="text-[color:var(--hero-gold)]">{PHRASES[index].slice(0, count)}</span>
          <span className="naumene-caret ml-0.5 inline-block h-[0.78em] w-[3px] translate-y-[0.06em] rounded-sm bg-[var(--hero-gold)]" />
        </span>
        {/* Reserves the box. Never read, never seen, never moves. */}
        <span className="col-start-1 row-start-1 opacity-0">Every {longest}</span>
      </span>
    </h1>
  );
}
